import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const rowCls = (active) =>
  `group relative flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 ${
    active ? "bg-white/[0.09] text-white" : "text-neutral-300 hover:bg-white/[0.05] hover:text-white"}`;

const MENU = "bg-[#161B28] border-white/10 min-w-[180px]";
const dotsCls = "absolute right-1 h-7 w-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100 outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70";

function RenameInput({ value, onSave, onDone, testid }) {
  const [v, setV] = useState(value);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const save = async () => {
    const t = v.trim();
    const changed = !!t && t !== value;
    if (changed) await onSave(t);
    onDone(changed);
  };
  return (
    <input ref={ref} value={v} onChange={(e) => setV(e.target.value)} onBlur={save} aria-label="Chat name" data-testid={testid} maxLength={60}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") onDone(false); }}
      className="w-full h-9 px-2.5 rounded-lg bg-white/[0.06] text-sm text-white outline-none ring-2 ring-sky-400/60" />
  );
}

const renameChat = async (chat, title) => {
  try { await http.patch(`/chats/${chat.chat_id}`, { title }); toast.success("Chat renamed"); }
  catch (e) { toast.error(formatError(e)); }
};

function RowMenu({ chat, onRename, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={`Options for ${chat.title}`} data-testid={`chat-menu-${chat.chat_id}`} className={dotsCls}>
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={MENU}>
        <DropdownMenuItem onClick={onRename} data-testid={`chat-rename-${chat.chat_id}`}><Pencil className="h-3.5 w-3.5 mr-2" />Rename</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete} className="text-red-300 focus:text-red-200" data-testid={`chat-delete-${chat.chat_id}`}><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// A pulsing dot while Bryn is still answering, a solid dot once a finished answer is waiting to be read.
function StatusDot({ chat, active }) {
  if (chat.generating) {
    return <span role="status" aria-label="Bryn is still answering" title="Bryn is still answering" data-testid={`chat-generating-${chat.chat_id}`}
      className="h-2 w-2 rounded-full bg-sky-400 animate-pulse shrink-0" />;
  }
  if (chat.answer_unseen && !active) {
    return <span role="status" aria-label="New answer ready" title="New answer ready" data-testid={`chat-ready-${chat.chat_id}`}
      className="h-2 w-2 rounded-full bg-sky-400 shrink-0 shadow-[0_0_0_3px_rgba(56,189,248,0.18)]" />;
  }
  return null;
}

export function ChatRow({ chat, onDelete, onPick, onChanged }) {
  const { chatId } = useParams();
  const [editing, setEditing] = useState(false);
  const active = chatId === chat.chat_id;
  if (editing) {
    return <RenameInput value={chat.title} testid={`chat-rename-input-${chat.chat_id}`}
      onSave={(t) => renameChat(chat, t)} onDone={(changed) => { setEditing(false); if (changed) onChanged(); }} />;
  }
  const del = () => { if (window.confirm(`Delete "${chat.title}"? This can't be undone.`)) onDelete(chat.chat_id); };
  const unread = chat.answer_unseen && !active;
  return (
    <div className={rowCls(active)}>
      <NavLink to={`/app/c/${chat.chat_id}`} onClick={onPick} data-testid={`chat-item-${chat.chat_id}`} title={chat.title}
        aria-current={active ? "page" : undefined} className="flex-1 min-w-0 flex items-center gap-2 pr-7 outline-none">
        <span className={`flex-1 min-w-0 truncate ${unread ? "text-white font-medium" : ""}`}>{chat.title}</span>
        <StatusDot chat={chat} active={active} />
      </NavLink>
      <RowMenu chat={chat} onRename={() => setEditing(true)} onDelete={del} />
    </div>
  );
}

const newest = (a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || ""));

export function SidebarRecents({ chats, onDelete, onPick, onChanged }) {
  const sorted = [...chats].sort(newest);
  return (
    <div data-testid="chat-list">
      <div className="flex items-center justify-between pl-2.5 pr-1 pt-5 pb-1.5">
        <p className="text-xs text-neutral-500">Recents</p>
        {chats.length > 0 && (
          <Link to="/app/chats" onClick={onPick} data-testid="sidebar-all-chats-link"
            className="text-xs text-neutral-500 hover:text-white rounded px-1 outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70">See all</Link>
        )}
      </div>
      {!sorted.length
        ? <p className="px-2.5 text-xs text-neutral-600" data-testid="recents-empty">Your chats will show up here</p>
        : sorted.map((c) => <ChatRow key={c.chat_id} chat={c} onDelete={onDelete} onPick={onPick} onChanged={onChanged} />)}
    </div>
  );
}
